import { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react'
import { Box, Button, Divider, Grid, IconButton, InputAdornment, Stack, TextField, Typography, Switch, Menu, MenuItem, MenuList } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import SecuritySearchModal from '../securitySearch/SecuritySearchModal'
import { prefetchSecurityCategories } from '../../services/apis/securities'
import { ExpressionCallbackData } from '../securitySearch/typings'
import { Watchlist, Watchlist_Type, Watchlist_WatchlistItem, Watchlist_WatchlistItem_Type } from '../../proto/generated/watchlist'
import { getSecurityKey } from '../../util/security'
import { convertExpressionToDisplayExpression, getExpressionFromSaved } from '../../util/expression'
import ExpressionNameDialog from './expressionNameDialog'
import { selectUserSecurityExpressions } from '../../selectors/security'
import commonActions from '../../actions/common.actions'
import { saveSecurityExpression } from '../../services/apis/expression'
import { addOrUpdateUserExpression } from '../../reducer/security.reducer'
import { selectCommunityWatchlists, selectPreviewWatchlist, selectPreviewWatchlistId, selectWatchlists } from '../../selectors/watchList'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CheckIcon from '@mui/icons-material/Check'
import WatchlistIcon from '../../assets/icons/watchlist'
import { makeStyles } from 'tss-react/mui'
import { setSecuritySearchOpen } from '../../reducer/home.reducer'
import PlusIcon from '../../assets/icons/PlusIcon'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { NonInteractiveTooltip } from '../custom'
import { selectUserDetails, selectUserPreferences, watchListView } from '../../selectors/user'
import WatchListCustomizeColumns from './WatchListCustomizeColumns'
import { updateUserPreferences } from '../../services/apis/user'
import { changeWatchlistSecurities, deleteWatchlist, fetchCommunityWatchList, toggleGroupViewInSourceWatchlist } from '../../services/apis/watchlist'
import { setCommunityWatchlists, setWatchlists } from '../../reducer/watchList.reducer'
import { updateWatchlist, updateWatchlistInState } from './WatchUtils'
import AddSectionIcon from '../../assets/icons/addSectionIcon'
import { selectPrimarySecurity } from '../../selectors/home'
import ROUTES from '../../assets/routes'
import { matchPath } from 'react-router-dom'
import DeleteIcon from '../../assets/icons/DeleteIcon'
import { useShouldAllowAccess } from '../../hooks/useWatchlistAccess'
import { useAdminScreenerAccess } from '../../hooks/useAdminScreenerAccess'
import { useHasAdminAccess } from '../../hooks/useHasAdminAccess'
import LockIcon from '../../assets/icons/lockIcon'
import { setFeatureRestrictionDialog } from '../../reducer/user.reducer'
import { FeatureCode } from '../../util/RestrictionFeatureCode'
interface WatchlistTopBarProps {
    onWatchlistSelected: Function
    onWatchlistSecuritiesChanged: Function
    onRenamed: Function
    modalOpen: boolean
    setIsModalOpened: Function
    onEditClicked: Function
    editMode: boolean
    setEditMode: React.Dispatch<React.SetStateAction<boolean>>
    communityWatchlistAccess: boolean
}

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: theme.spacing(0, 1),
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    fullStack: {
        width: '100%'
    },
    watchlistButton: {
        borderRadius: '2px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        '& .MuiTypography-root': {
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        }
    },
    watchlistName: {
        marginRight: theme.spacing(1),
        width: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: '0.875rem'
    },
    editIcon: {
        fontSize: 18,
        marginLeft: theme.spacing(1)
    },
    addButton: {
        padding: theme.spacing(0.25, 1)
    },
    textField: {
        '& .MuiInputBase-input': {
            paddingY: theme.spacing(1)
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
            marginX: theme.spacing(1)
        }
    }
}))

function WatchListTopBar(props: WatchlistTopBarProps) {
    const { key, expressionSecurities, expressionMode } = useSelector(selectPrimarySecurity)
    const { communityWatchlistAccess } = props
    const watchlists = useSelector(selectWatchlists)
    const communityWatchlists = useSelector(selectCommunityWatchlists)
    const shouldAllowAccess = useShouldAllowAccess()
    const shouldAllowAdminAccess = useAdminScreenerAccess()
    const { onRenamed, onWatchlistSecuritiesChanged, onEditClicked, modalOpen, setIsModalOpened, editMode, setEditMode } = props
    const adminFlag = useHasAdminAccess()
    const [renameWatchlist, setRenameWatchlist] = useState<boolean>(false)
    const [openExpressionNameDialog, setOpenExpressionNameDialog] = useState<boolean>(false)
    const [expressionNameCallbackData, setExpressionNameCallbackData] = useState<Watchlist_WatchlistItem | undefined>()
    const savedExpressions = useSelector(selectUserSecurityExpressions)
    const [mouseEnter, setMouseEnter] = useState<boolean>(false)
    const dispatch = useDispatch()
    const { classes } = useStyles()
    const tableViews = useSelector(watchListView)
    const userPreferences = useSelector(selectUserPreferences)
    const dataUpdating = useRef<boolean>(false)
    const [loading, setLoading] = useState<boolean>(true)
    const [groupView, setGroupView] = useState<boolean>(false)
    const previewId = useSelector(selectPreviewWatchlistId)
    const previewWatchlist = useSelector(selectPreviewWatchlist)
    const selectedWatchlistIdFromStore = useSelector((state: RootState) => state.commonStore.watchlistId)
    const selectedWatchlistId = useMemo(() => {
        if (previewId.isActive && previewId.watchlistId) {
            return previewId.watchlistId
        }
        return selectedWatchlistIdFromStore
    }, [previewId, selectedWatchlistIdFromStore])
    const selectedWatchlist = useMemo(() => {
        if (previewId.isActive && previewWatchlist) {
            return previewWatchlist
        }
        return watchlists[selectedWatchlistId]
    }, [previewId.isActive, previewWatchlist, watchlists, selectedWatchlistId])
    const watchlistEditedName = useRef<string | undefined>(selectedWatchlist?.name ?? '')

    const srcWatchlist = useMemo(() => communityWatchlists?.find((wl) => wl.shareId === selectedWatchlist?.shareId), [communityWatchlists, selectedWatchlist?.shareId])

    useEffect(() => {
        if (!selectedWatchlist) return
        if (srcWatchlist?.screenerId && srcWatchlist.screenerId.trim() !== '') {
            setGroupView(srcWatchlist?.groupView ?? selectedWatchlist?.groupView ?? false)
        }
    }, [selectedWatchlist, communityWatchlists])

    const isMatching = useMemo(() => {
        if (!selectedWatchlist) return false

        if (expressionMode) {
            const exprSecurities = expressionSecurities || []

            const itemSecurities = selectedWatchlist.watchlistItems.flatMap((item: any) => item.item.securityExpression?.securities || [])

            const sortedExprCodes = exprSecurities.map((s) => s.code).sort()
            const sortedItemCodes = itemSecurities.map((s) => s.code).sort()

            if (sortedExprCodes.length !== sortedItemCodes.length) return false

            return sortedExprCodes.every((code, i) => code === sortedItemCodes[i])
        }
        const securityKeys = selectedWatchlist.watchlistItems
            .filter((item) => item.item.type === Watchlist_WatchlistItem_Type.SECURITY)
            .map((item) => {
                const sec = item.item.security
                return sec ? getSecurityKey(sec.exchange, sec.code) : null
            })
            .filter(Boolean)

        return securityKeys.includes(key)
    }, [expressionMode, expressionSecurities, selectedWatchlist, key])
    const handleToggle = () => {
        updateUserPreferences({
            body: {
                watchlist: {
                    ...userPreferences.watchlist,
                    tableView: !userPreferences.watchlist.tableView
                }
            }
        })
    }
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleGroupByToggle = useCallback(async () => {
        if (!srcWatchlist?.screenerId?.trim()) return
        const newValue = !groupView
        try {
            await toggleGroupViewInSourceWatchlist(srcWatchlist.id, newValue)
            setGroupView(newValue)
            dispatch(
                setCommunityWatchlists({
                    ...communityWatchlists,
                    watchlists: communityWatchlists.map((wl) => (wl.id === srcWatchlist.id ? { ...wl, groupView: newValue } : wl))
                })
            )
            await updateWatchlistInState(selectedWatchlist.id, dispatch)
        } catch (err) {
            console.error('Failed to toggle groupView:', err)
        }
    }, [communityWatchlists, dispatch, groupView, srcWatchlist])

    const handleAddSection = useCallback(async () => {
        if (!selectedWatchlist) return
        const allSectionNames = new Set(
            Object.values(watchlists).flatMap(
                (wl) => wl.watchlistItems?.filter((item) => item.item.type === Watchlist_WatchlistItem_Type.SECTION).map((section) => section.item.sectionName?.trim()) || []
            )
        )
        let sectionIndex = 1

        const baseName = 'Section'
        let generatedName = `${baseName} ${sectionIndex}`
        while (allSectionNames.has(generatedName)) {
            sectionIndex++
            generatedName = `${baseName} ${sectionIndex}`
        }

        const items = selectedWatchlist.watchlistItems
        let insertIndex = items.length
        let newPriority = 0

        if (isMatching) {
            insertIndex = items.findIndex((item) => {
                if (item.item.type !== Watchlist_WatchlistItem_Type.SECURITY) return false
                const sec = item.item.security
                return sec && getSecurityKey(sec.exchange, sec.code) === key
            })

            if (insertIndex === -1) {
                insertIndex = items.length
            }
            const before = items[insertIndex - 1]
            const current = items[insertIndex]
            const beforePriority = before?.item?.priority ?? (current?.item?.priority ?? 0) - 1
            const currentPriority = current?.item?.priority ?? beforePriority + 1
            newPriority = (beforePriority + currentPriority) / 2
        } else {
            newPriority = (items[items.length - 1]?.item?.priority ?? 0) + 1
        }
        const newSection: Watchlist_WatchlistItem = {
            type: Watchlist_WatchlistItem_Type.SECTION,
            sectionName: generatedName,
            priority: newPriority
        }
        const newItem = {
            id: `${Date.now()}`,
            item: newSection
        }
        const updatedItems = [...items]
        updatedItems.splice(insertIndex, 0, newItem)
        dispatch(
            setWatchlists({
                ...watchlists,
                [selectedWatchlist.id]: {
                    ...selectedWatchlist,
                    watchlistItems: updatedItems
                }
            })
        )

        try {
            await changeWatchlistSecurities({
                watchlistItem: newSection,
                watchlistId: selectedWatchlist.id,
                method: 'POST'
            })
            await updateWatchlist(dispatch, selectedWatchlistId, dataUpdating, setLoading, false, true, false)
        } catch (error) {
            console.error(error)
        }
    }, [selectedWatchlist, watchlists, isMatching, dispatch, key, selectedWatchlistId])
    const handleDelete = useCallback(async () => {
        if (selectedWatchlist) {
            try {
                await deleteWatchlist({ dispatch, watchlistId: selectedWatchlist.id })
                updateWatchlist(dispatch, selectedWatchlist.id, dataUpdating, setLoading, false, true, true)
                handleClose()
            } catch (err) {
                console.error('Error deleting watchlist:', err)
            }
        }
    }, [selectedWatchlist, dispatch])

    const onExpressionNameDialogClosed = useCallback(
        (name: string | null) => {
            if (name !== null && expressionNameCallbackData?.securityExpression) {
                saveSecurityExpression({
                    expression: {
                        ...expressionNameCallbackData.securityExpression,
                        name: name
                    }
                }).then((response) => {
                    dispatch(addOrUpdateUserExpression(response))
                    commonActions.deleteRecentExpression(dispatch, response.expression)
                    onWatchlistSecuritiesChanged(
                        {
                            ...expressionNameCallbackData,
                            securityExpression: response
                        },
                        true,
                        selectedWatchlistId
                    )
                })
            }
            setOpenExpressionNameDialog(false)
        },
        [expressionNameCallbackData, dispatch, onWatchlistSecuritiesChanged, selectedWatchlistId]
    )

    const onSecuritySearchModalClose = useCallback(() => {
        setIsModalOpened(false)
    }, [setIsModalOpened])

    const onSecurityClicked = useCallback(
        (data: ExpressionCallbackData, selected: boolean) => {
            if (data && data.securities && data.securities.length > 0) {
                const savedExpression = getExpressionFromSaved(savedExpressions, data.expression)
                const isExpressionMode = data.expression.length > 0 && ['STRATEGY_EXPRESSION', 'EXPRESSION'].includes(data.type ?? '')
                let priority = undefined
                if (selected) {
                    const isRouteChart = matchPath(ROUTES.chart, window.location.pathname)
                    if (isRouteChart) {
                        priority = (selectedWatchlist?.watchlistItems?.[selectedWatchlist?.watchlistItems?.length - 1]?.item?.priority ?? 0) + 1
                    }
                }

                if (isExpressionMode) {
                    onSecuritySearchModalClose()
                    dispatch(setSecuritySearchOpen({ open: false, key: undefined, isExpression: false }))
                }

                if(!communityWatchlistAccess && isExpressionMode){
                    dispatch(setFeatureRestrictionDialog({
                        open: true,
                        feature: "Security expressions",
                        featureRestrictionCode:FeatureCode.SECURITY_EXPRESSIONS
                    }))
                    return
                }

                if (isExpressionMode && selected && data.type !== 'STRATEGY_EXPRESSION' && (savedExpression === undefined || savedExpression.name === undefined)) {
                    setExpressionNameCallbackData({
                        type: Watchlist_WatchlistItem_Type.EXPRESSION,
                        security: undefined,
                        securityExpression: {
                            id: data.expressionId ?? data.expression,
                            name: '',
                            expression: data.expression,
                            securities: data.securities,
                            expired: false
                        }
                    })
                    setOpenExpressionNameDialog(true)
                } else {
                    if (isExpressionMode && data.type === 'STRATEGY_EXPRESSION') {
                        props.onWatchlistSecuritiesChanged(
                            {
                                type: Watchlist_WatchlistItem_Type.EXPRESSION,
                                security: undefined,
                                securityExpression: {
                                    id: data.expressionId ?? data.expression,
                                    expression: data.expression,
                                    name: data.name ?? '',
                                    securities: data.securities,
                                    expired: true
                                },
                                priority
                            },
                            selected,
                            selectedWatchlistId
                        )
                        return
                    }
                    if (isExpressionMode && !savedExpression) {
                        commonActions.setApiError(dispatch, {
                            error: true,
                            message: 'Something went wrong',
                            errorType: 'error'
                        })
                        return
                    }
                    const watchlistItem: Watchlist_WatchlistItem = isExpressionMode
                        ? {
                              type: Watchlist_WatchlistItem_Type.EXPRESSION,
                              security: undefined,
                              securityExpression: savedExpression,
                              priority
                          }
                        : {
                              type: Watchlist_WatchlistItem_Type.SECURITY,
                              security: data.securities[0],
                              securityExpression: undefined,
                              priority
                          }
                    props.onWatchlistSecuritiesChanged(watchlistItem, selected, selectedWatchlistId)
                }
            }
        },
        [savedExpressions, onSecuritySearchModalClose, dispatch, selectedWatchlist?.watchlistItems, props, selectedWatchlistId, communityWatchlistAccess]
    )

    const onRenameClicked = useCallback(() => {
        if (selectedWatchlist && !shouldAllowAccess(selectedWatchlist, editMode)) return
        setRenameWatchlist(true)
    }, [selectedWatchlist, editMode])

    useEffect(() => {
        onEditClicked(renameWatchlist)
    }, [renameWatchlist])

    const onMouseEnter = useCallback(() => {
        setMouseEnter(true)
    }, [])

    const onMouseLeave = useCallback(() => {
        setMouseEnter(false)
    }, [])

    const selectedItems = useMemo((): Set<string> => {
        const set = new Set<string>()
        if (!selectedWatchlist || !selectedWatchlist.watchlistItems) return set

        selectedWatchlist.watchlistItems.forEach((watchlistItem) => {
            const { type, security, securityExpression } = watchlistItem.item
            if (type === Watchlist_WatchlistItem_Type.SECURITY) {
                if (!security) {
                    return
                }
                set.add(getSecurityKey(security.exchange, security.code))
            } else if (type === Watchlist_WatchlistItem_Type.EXPRESSION) {
                if (!securityExpression) {
                    return
                }
                set.add(securityExpression.expression)
            }
        })
        return set
    }, [selectedWatchlist])

    const onSecuritySearchModalOpen = useCallback(() => {
        prefetchSecurityCategories()
        setIsModalOpened(true)
    }, [setIsModalOpened])

    const onWatchlistNameChange = useCallback((e: any) => {
        watchlistEditedName.current = e.target.value
    }, [])

    const onKeyDown = useCallback(
        (e: any) => {
            if (e.key === 'Enter') {
                if (!watchlistEditedName.current || watchlistEditedName.current?.trim().length === 0 || watchlistEditedName.current === selectedWatchlist?.name) {
                    watchlistEditedName.current = undefined
                    setRenameWatchlist(false)
                    return
                } else {
                    onRenamed(watchlistEditedName.current)
                }
                watchlistEditedName.current = undefined
                setRenameWatchlist(false)
            } else if (e.key === 'Escape') {
                watchlistEditedName.current = undefined
                setRenameWatchlist(false)
            }
        },
        [selectedWatchlist, onRenamed]
    )

    return (
        <>
            <Grid container>
                <Grid container className={classes.container}>
                    {!renameWatchlist ? (
                        <Stack className={classes.fullStack} direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row">
                                <WatchlistIcon sx={{ color: (theme) => theme.palette.text.secondary }} />
                                <Button disableRipple color="inherit" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onClick={onRenameClicked} className={classes.watchlistButton}>
                                    <Typography className={classes.watchlistName}>{selectedWatchlist?.name ?? ''}</Typography>
                                    {selectedWatchlist?.shareId ? <LockIcon sx={{ fontSize: '10px' }} /> : null}
                                    {selectedWatchlist && shouldAllowAccess(selectedWatchlist, editMode) && mouseEnter && <EditOutlinedIcon className={classes.editIcon} />}
                                </Button>
                            </Stack>
                            <Stack direction="row" spacing={1}>
                                {selectedWatchlist && shouldAllowAccess(selectedWatchlist, editMode) && shouldAllowAdminAccess(selectedWatchlist?.shareId) && (
                                    <NonInteractiveTooltip title={'Add Security'} arrow placement="top">
                                        <Button variant="outlined" color="inherit" onClick={onSecuritySearchModalOpen} size="small" sx={{ minWidth: '32px' }}>
                                            {<PlusIcon fontSize="small" />}
                                        </Button>
                                    </NonInteractiveTooltip>
                                )}
                                <NonInteractiveTooltip title={'Settings'} arrow placement="top">
                                    <Button variant="outlined" color="inherit" size="small" onClick={handleOpen} sx={{ minWidth: '32px' }}>
                                        {' '}
                                        {<MoreHorizIcon fontSize="small" />}
                                    </Button>
                                </NonInteractiveTooltip>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleClose}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                    PaperProps={{
                                        sx: {
                                            minWidth: 180,
                                            paddingTop: '0!important'
                                        }
                                    }}
                                    MenuListProps={{
                                        sx: {
                                            padding: '0 !important'
                                        }
                                    }}
                                >
                                    <MenuList dense sx={{ padding: '0 !important' }}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%', paddingLeft: '10px', height: '36px' }}>
                                            <Typography variant="body2">Table View</Typography>
                                            <Switch size="small" checked={tableViews} onChange={handleToggle} color="primary" style={{ marginRight: 1 }} />
                                        </Stack>
                                        {selectedWatchlist && srcWatchlist?.screenerId && srcWatchlist.screenerId.trim() !== '' && shouldAllowAccess(selectedWatchlist, true) && (
                                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%', paddingLeft: '10px', height: '36px' }}>
                                                <Typography variant="body2">Group View</Typography>
                                                <Switch size="small" checked={groupView} onChange={handleGroupByToggle} color="primary" style={{ marginRight: 1 }} />
                                            </Stack>
                                        )}
                                        {adminFlag && selectedWatchlist?.type.includes(Watchlist_Type.REFERENCE) && shouldAllowAdminAccess(selectedWatchlist?.shareId) && (
                                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%', paddingLeft: '10px', height: '36px' }}>
                                                <Typography variant="body2">Edit Mode</Typography>
                                                <Switch size="small" checked={editMode} onChange={() => setEditMode(!editMode)} color="primary" style={{ marginRight: 1 }} />
                                            </Stack>
                                        )}
                                        {selectedWatchlist && shouldAllowAccess(selectedWatchlist, editMode) && shouldAllowAdminAccess(selectedWatchlist?.shareId) && (
                                            <MenuItem onClick={handleAddSection}>
                                                <IconButton sx={{ padding: '0px', mr: '10px' }}>
                                                    <AddSectionIcon />
                                                </IconButton>
                                                Add Section
                                            </MenuItem>
                                        )}
                                        {selectedWatchlist && shouldAllowAccess(selectedWatchlist, editMode) && !selectedWatchlist.default && (
                                            <MenuItem onClick={handleDelete}>
                                                <IconButton sx={{ padding: '0px', mr: '10px' }}>
                                                    <DeleteIcon style={{ fontSize: '20px' }} />
                                                </IconButton>
                                                Delete
                                            </MenuItem>
                                        )}

                                        {tableViews && (
                                            <Box>
                                                <WatchListCustomizeColumns />
                                            </Box>
                                        )}
                                    </MenuList>
                                </Menu>
                            </Stack>
                        </Stack>
                    ) : (
                        <Box className={classes.fullStack}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                multiline={false}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => {
                                                    if (!watchlistEditedName.current || watchlistEditedName.current?.trim().length === 0 || watchlistEditedName.current === selectedWatchlist?.name) {
                                                        watchlistEditedName.current = undefined
                                                        setRenameWatchlist(false)
                                                        return
                                                    } else {
                                                        onRenamed(watchlistEditedName.current)
                                                    }
                                                    watchlistEditedName.current = undefined
                                                    setRenameWatchlist(false)
                                                }}
                                            >
                                                <CheckIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                defaultValue={selectedWatchlist?.name ?? ''}
                                onChange={onWatchlistNameChange}
                                onKeyDown={onKeyDown}
                                autoFocus
                                className={classes.textField}
                            />
                        </Box>
                    )}
                </Grid>
                <Divider className={classes.fullStack} />
            </Grid>
            {modalOpen && (
                <SecuritySearchModal
                    open={modalOpen}
                    enableExpressions={true}
                    onCloseClicked={onSecuritySearchModalClose}
                    onSecuritySelected={onSecurityClicked}
                    selectedItems={selectedItems}
                    tailIcon
                    watchlistMode
                />
            )}
            {openExpressionNameDialog && <ExpressionNameDialog onClosed={onExpressionNameDialogClosed} />}
        </>
    )
}

export default memo(WatchListTopBar)
